import React, { Component } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import StateLink from '../../StateLink';
import { selectors } from '../../../store/CurrentUser.model';
import ssPropTypes from '../../../modules/ssPropTypes';
import withStore from '../../withStore';
import ExternalLink from '../../ui/ExternalLink';
import { addQueryStringParams } from '../../../modules/url';

import './AccountMenu.scss';

/**
 * AccountMenu - the drop down menu in the header presented to the client when logged in.
 *
 * Usage
 * ```jsx
 * <AccountMenu />
 * ```
 * This component takes props, described below, but is connected to the redux store
 * and therefore needs no props to be explicitly passed.
 *
 */
class AccountMenu extends Component {
  static propTypes = {
    /** logout: (Function|Required)
     *  function that when called causes the current user to be logged out. */
    logout: PropTypes.func.isRequired,
    /** isLoggedIn: (Boolean|Required) whether the current user is already logged in */
    isLoggedIn: PropTypes.bool.isRequired,
    /** {String} The provider ID of the current user */
    providerId: ssPropTypes.numberLike,
    /** Object of utms */
    utmParams: PropTypes.object,
  };

  static defaultProps = {
    providerId: null,
    utmParams: {},
  };

  state = {
    dropDownOpen: false,
  };

  /**
   * Toggles the Drop Down state
   */
  toggleDropDown = () => {
    this.setState(prevState => ({
      dropDownOpen: !prevState.dropDownOpen,
    }));
  };

  /**
   * Opens the Drop Down
   */
  openDropDown = () => {
    this.setState({
      dropDownOpen: true,
    });
  };

  /**
   * Closes the Drop Down
   */
  closeDropDown = () => {
    this.setState({
      dropDownOpen: false,
    });
  };

  /**
   * Logs out the user
   */
  logoutUser = () => {
    this.props.logout()
      .then(() => {
        window.location.assign('/');
      });
  };

  render() {
    if (this.props.isLoggedIn && !this.props.providerId) {
      return (
        <div
          className="account-menu-component sbp-state"
          onClick={() => this.toggleDropDown()}
          onKeyDown={() => this.toggleDropDown()}
          role="presentation"
        >
          <div
            className={classNames(
              'settings',
              { 'menu-open': this.state.dropDownOpen },
            )}
            onMouseEnter={() => this.openDropDown()}
            onMouseLeave={() => this.closeDropDown()}
          >
            <span className="settings-ctrl">
              My Settings
            </span>
            {this.state.dropDownOpen && (
              <ul className="menu">
                <li><StateLink href="/m/my-account">My Account</StateLink></li>
                <li><StateLink href="/m/appointments">My Appointments</StateLink></li>
                <li>
                  <ExternalLink
                    href={addQueryStringParams('https://www.styleseat.com/blog/', this.props.utmParams)}
                    trackingParams={{
                      eventName: 'client_user_dropdown_blog_clicked',
                    }}
                  >
                    Beauty Blog
                  </ExternalLink>
                </li>
                <li>
                  <ExternalLink
                    target=""
                    href={addQueryStringParams('/join', {
                      source: 'client_user_dropdown',
                      ...this.props.utmParams,
                    })}
                  >
                    Set Up My Business
                  </ExternalLink>
                </li>
                <li>
                  <button type="button" onClick={() => this.logoutUser()}>Logout</button>
                </li>
              </ul>
            )}
          </div>
        </div>
      );
    }

    return null;
  }
}

function mapStateToProps(state) {
  return {
    isLoggedIn: selectors.isLoggedIn(state),
    fullName: selectors.fullName(state),
    providerId: selectors.getLoggedInProviderId(state),
    utmParams: {
      utm_source: state.route.params.utm_source,
      utm_campaign: state.route.params.utm_campaign,
      utm_content: state.route.params.utm_content,
      utm_medium: state.route.params.utm_medium,
      utm_term: state.route.params.utm_term,
    },
  };
}

function mapDispatchToProps(dispatch) {
  return {
    logout: dispatch.user.logout,
  };
}

export default withStore(connect(mapStateToProps, mapDispatchToProps)(AccountMenu));
